// "Price changed at this store": compares a basket item with the user's own
// most recent earlier purchase of the same product at the same store. Pure and
// cheap, so the basket recomputes it whenever the item, the store or the
// history changes. Uses the user's own trips only (every plan), not the Lattice.

import { computePricePerUnit } from './intelligence';
import { streetPart } from '../../utils/storeUtils';

export interface StorePriceChange {
  /** The earlier price, per unit when `unit` is set, otherwise per item. */
  previousPrice: number;
  currentPrice: number;
  /** current - previous (positive: went up). */
  delta: number;
  previousDate: string;
  /** Set when the comparison is per unit (both sizes known, same unit). */
  unit: string | null;
}

interface PriceItemLike {
  name?: string | null;
  price?: number | null;
  size?: number | string | null;
  unit?: string | null;
  canonicalId?: string | null;
}
interface TripLike {
  date: string;
  items?: PriceItemLike[] | null;
  storeInfo?: { id?: string | null; name?: string | null; address?: string | null } | null;
}
interface StoreLike { id?: string | null; name?: string | null; address?: string | null }

// Ignore rounding noise: a change must be at least a cent and at least 1%.
const MIN_DELTA = 0.01;
const MIN_RELATIVE = 0.01;
// More than 10x either way is almost certainly a misread price or quantity, not a
// price change; the basket's "Check price" uses the same ratio (latticeBenchmark).
const MAX_RATIO = 10;

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();

export const isSameStore = (a: StoreLike | null | undefined, b: StoreLike | null | undefined): boolean => {
  if (!a || !b) return false;
  if (a.id && b.id) return a.id === b.id;
  if (!a.name || norm(a.name) !== norm(b.name)) return false;
  // Same chain name: also require the same street when both addresses are known.
  if (a.address && b.address) return norm(streetPart(a.address)) === norm(streetPart(b.address));
  return true;
};

// archive_trip appends a suffix to a slug that already exists (often just a
// trailing "-"), so the same product can come back as "corn-16oz" and
// "corn-16oz-". Compare slugs without trailing dashes, or the exact same name.
const slugKey = (slug: string | null | undefined) => norm(slug).replace(/-+$/, '');
const isSameProduct = (a: PriceItemLike, b: PriceItemLike): boolean =>
  (!!a.canonicalId && !!b.canonicalId && slugKey(a.canonicalId) === slugKey(b.canonicalId))
  || (!!a.name && !!b.name && norm(a.name) === norm(b.name));

const comparablePrice = (item: PriceItemLike, unit: string | null): number | null => {
  const price = Number(item.price);
  if (!(price > 0)) return null;
  return unit ? computePricePerUnit(price, item.size as any) : price;
};

export function findStorePriceChange(
  item: PriceItemLike,
  history: TripLike[],
  store: StoreLike | null | undefined,
): StorePriceChange | null {
  if ((!item.canonicalId && !item.name) || !store?.name || !(Number(item.price) > 0)) return null;
  // Newest first.
  const trips = [...history].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  for (const trip of trips) {
    if (!isSameStore(trip.storeInfo, store)) continue;
    const previous = (trip.items || []).find(p => isSameProduct(p, item) && Number(p.price) > 0);
    if (!previous) continue;

    // Same pack: compare the price itself ("Up $0.20"). A different pack size in
    // the same unit: compare per unit. Anything else isn't comparable, so a
    // different pack size is never called a price change.
    const sameUnit = !!item.unit && !!previous.unit && norm(item.unit) === norm(previous.unit);
    const a = parseFloat(String(item.size ?? '')), b = parseFloat(String(previous.size ?? ''));
    const samePack = (Number.isFinite(a) && Number.isFinite(b)) ? (Math.abs(a - b) < 1e-9 && (sameUnit || !item.unit || !previous.unit)) : norm(item.size) === norm(previous.size);
    const unit = !samePack && sameUnit && computePricePerUnit(1, item.size as any) != null && computePricePerUnit(1, previous.size as any) != null
      ? item.unit! : null;
    if (!samePack && !unit) return null;

    const current = comparablePrice(item, unit);
    const before = comparablePrice(previous, unit);
    if (current == null || before == null) return null;
    const delta = current - before;
    if (Math.abs(delta) < MIN_DELTA || Math.abs(delta) / before < MIN_RELATIVE) return null;
    if (current / before > MAX_RATIO || before / current > MAX_RATIO) return null;
    return { previousPrice: before, currentPrice: current, delta, previousDate: trip.date, unit };
  }
  return null;
}
